import { cn } from '@/lib/utils';

export type LoadingVariant = 'skeleton' | 'spinner' | 'inline';

export interface LoadingStateProps {
  variant: LoadingVariant;
  rows?: number;
  label?: string;
  className?: string;
}

/**
 * Used in EVERY data-loading context per CLAUDE.md §3 + §28.9.
 * Variants:
 *   - skeleton: stacked grey bars (use `rows` to control count)
 *   - spinner:  centered spinner
 *   - inline:   small inline indicator next to text
 */
export function LoadingState({
  variant,
  rows = 3,
  label = 'Lädt …',
  className,
}: LoadingStateProps): JSX.Element {
  if (variant === 'skeleton') {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={label}
        data-variant="skeleton"
        className={cn('space-y-2', className)}
      >
        {Array.from({ length: rows }).map((_unused, i) => (
          <div key={i} className="h-4 w-full animate-pulse rounded bg-muted" aria-hidden />
        ))}
      </div>
    );
  }

  if (variant === 'spinner') {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={label}
        data-variant="spinner"
        className={cn('flex items-center justify-center p-8', className)}
      >
        <span className="sr-only">{label}</span>
        <span
          aria-hidden
          className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary"
        />
      </div>
    );
  }

  return (
    <span
      role="status"
      aria-live="polite"
      aria-label={label}
      data-variant="inline"
      className={cn('inline-flex items-center gap-2 text-sm text-muted-foreground', className)}
    >
      <span
        aria-hidden
        className="h-3 w-3 animate-spin rounded-full border border-muted border-t-primary"
      />
      {label}
    </span>
  );
}
